import { ServiceBase } from "../ServiceBase";
import { IGameAnimatedIconsManagerService, IGameAnimatedIconRequestOptions, IGameAnimatedIconRegistrationResult, GameAnimatedIconUnloadReason } from "./game-animated-icons-manager.service.interface";
import { IServiceFactoryExtended } from "../service-factory-extended.interface";
import { shuffle } from "../../../utils/array-helper";

const MAX_ANIMATED_ICONS = 2;

type GameAnimatedIconObservableTarget = IGameAnimatedIconRequestOptions & {
    animationDisposer?(reason: GameAnimatedIconUnloadReason): void;
    inViewport?: boolean;
    loadingInProgress?: boolean;
    abortLoading?: boolean;
};

/**
 * This service will manage game animated icons loaded with pixi-spine.
 *
 */
export class GameAnimatedIconsManagerService extends ServiceBase implements IGameAnimatedIconsManagerService {
    private intersectionObserver: IntersectionObserver | null = null;
    private observedTargets = new Map<Element, GameAnimatedIconObservableTarget>();
    private isGameOpened: boolean = false;

    constructor(services: IServiceFactoryExtended) {
        super(services);
        this.intersectionObserver = new IntersectionObserver(this.intersectionCallback, {
            threshold: 0.7
        });
        /**
         * When the game opens, the content is not visible so stop all animations
         */
        services.sdk.events.onGameOpen(() => {
            this.isGameOpened = true;
            this.observedTargets.forEach((observedTarget) => {
                this.attemptDisposeAnimation(observedTarget, GameAnimatedIconUnloadReason.GAME_OPEN);
            });
        });
        /**
         * When the game opens, the content is visible so load animations
         */
        services.sdk.events.onGameClose(() => {
            this.isGameOpened = false;
            this.selectAndLoadAnimations();
        });
    }

    private intersectionCallback = (entries: IntersectionObserverEntry[], observer: IntersectionObserver) => {
        entries.forEach((entry) => {
            if (!this.observedTargets.has(entry.target)) {
                // should throw error ?
                return;
            }
            const observedTarget = this.observedTargets.get(entry.target);
            if (entry.isIntersecting) {
                // mark the observedTarget as eligible for receiving animation
                observedTarget.inViewport = true;
            } else {
                // unmark the observedTarget as eligible targets
                observedTarget.inViewport = false;
                // destroy the animation if it exists
                this.attemptDisposeAnimation(observedTarget, GameAnimatedIconUnloadReason.VIEWPORT_EXIT);
            }
        });
        this.selectAndLoadAnimations();
    };

    /**
     * Method to register the game animated icon so it can be managed by this.
     */
    public register(options: IGameAnimatedIconRequestOptions): IGameAnimatedIconRegistrationResult {
        this.observedTargets.set(options.wrapper, options);
        this.intersectionObserver.observe(options.wrapper);

        return {
            unregister: () => {
                if (this.observedTargets.has(options.wrapper)) {
                    const observedTarget = this.observedTargets.get(options.wrapper)
                    this.observedTargets.delete(options.wrapper);
                    this.intersectionObserver.unobserve(options.wrapper);
                    this.attemptDisposeAnimation(observedTarget, GameAnimatedIconUnloadReason.REQUESTED);
                }
            }
        };
    }

    private attemptDisposeAnimation(observedTarget: GameAnimatedIconObservableTarget, reason: GameAnimatedIconUnloadReason) {
        if (observedTarget.animationDisposer) {
            if (observedTarget.onAnimationBeforeUnload) {
                observedTarget.onAnimationBeforeUnload(reason);
            }
            observedTarget.animationDisposer(reason);
            observedTarget.animationDisposer = undefined;
            if (observedTarget.onAnimationUnload) {
                observedTarget.onAnimationUnload(reason);
            }
        } if (observedTarget.loadingInProgress) {
            // we cannot actually abort loading the resources (json, png, atlas) because they are xhr calls
            // so we set this flag and after the xhr is done we do not start the animation and we reset the flag
            observedTarget.abortLoading = true;
        }
    }

    private debounceTimeout: number | undefined;
    private selectionRunning: boolean = false;
    private selectionRunAgainNeeded: boolean = false;
    private selectAndLoadAnimations = () => {
        // we cannot run the selection twice in paralel, it has to be sequential
        if (this.selectionRunning) {
            this.selectionRunAgainNeeded = true;
            return;
        }
        // avoid choking the CPU with too many subsequent tasks, debounce
        if (this.debounceTimeout) {
            clearTimeout(this.debounceTimeout);
            this.debounceTimeout = undefined;
        }
        this.debounceTimeout = setTimeout(async () => {
            this.selectionRunning = true;

            // When a game is opened, we don't load any animation
            if (!this.isGameOpened) {
                let alreadyRunningAnimationCount = 0;
                let eligibleTargets: GameAnimatedIconObservableTarget[] = [];
                // count already running animations and count eligible targets
                this.observedTargets.forEach((observedTarget) => {
                    if (observedTarget.animationDisposer || observedTarget.loadingInProgress) {
                        alreadyRunningAnimationCount++;
                    }
                    if (observedTarget.inViewport && !observedTarget.animationDisposer && !observedTarget.loadingInProgress) {
                        eligibleTargets.push(observedTarget);
                    }
                });

                // if we are not already running the maximum allowed animations we take as much as we can from the eligibles targets
                if (alreadyRunningAnimationCount > MAX_ANIMATED_ICONS) {
                    this.services.logger.error("There are more animated icons running ("+alreadyRunningAnimationCount+") than MAX_ANIMATED_ICONS ("+MAX_ANIMATED_ICONS+")");
                } else if (alreadyRunningAnimationCount < MAX_ANIMATED_ICONS) {
                    let remainingSlots = MAX_ANIMATED_ICONS - alreadyRunningAnimationCount;
                    shuffle(eligibleTargets);
                    for (let i=0; i<Math.min(eligibleTargets.length, remainingSlots); i++) {
                        const observedTarget = eligibleTargets[i];
                        this.loadGameAnimatedIcon(observedTarget);
                    }
                }
            }

            this.debounceTimeout = undefined;
            this.selectionRunning = false;
            if (this.selectionRunAgainNeeded) {
                this.selectionRunAgainNeeded = false;
                this.selectAndLoadAnimations();
            }
        }, 400);
    }

    private async loadGameAnimatedIcon(options: GameAnimatedIconObservableTarget) {
        if (options.loadingInProgress) {
            throw new Error("Animated Icon Loading in progress already for this icon. Please be patient");
        }
        try {
            options.loadingInProgress = true;
            let {app: pixiApp, dispose: disposePixiApp, PixiSpine, canvas} = await this.services.pixiApp.createPixiApp({
                width: options.size.width, height: options.size.height,
                onContextLost: () => {
                    this.attemptDisposeAnimation(options, GameAnimatedIconUnloadReason.CONTEXT_LOST);
                }
            });
            canvas.style.display = "block";
            canvas.style.width = "100%";

            const {resources} = await this.services.pixiApp.loadResources(pixiApp, [{name: 'gameAnimatedIconResource', jsonUrl: options.jsonUrl}]).catch((e: unknown) => {
                disposePixiApp();
                throw e;
            });

            let spineAnimation = new PixiSpine.Spine(resources.gameAnimatedIconResource.spineData);

            // get the animations
            const availableAnimations = resources.gameAnimatedIconResource.spineData.animations.map(a => a.name);

            // play animation
            const runAnimation = () => {
                // animated icons will have only one animation that we play in loop. There won't be complex timeline animations
                spineAnimation.state.setAnimation(0, availableAnimations[0], false);
            }
            let animationSchedule: number;

            spineAnimation.state.addListener({
                complete: (entry) => {
                    // when animation completes, schedule the next iteration
                    animationSchedule = setTimeout(runAnimation, options.timings.interval);
                }
            });

            runAnimation();

            const animationDisposer = () => {
                clearTimeout(animationSchedule);
                spineAnimation.destroy();
                animationSchedule = undefined;
                spineAnimation = undefined;
                disposePixiApp();
            };
            if (options.abortLoading) {
                animationDisposer();
                options.abortLoading = false;
            } else {
                options.animationDisposer = animationDisposer;
                // add the animation to the scene and render...
                pixiApp.stage.addChild(spineAnimation);
                options.wrapper.appendChild(pixiApp.view);

                if (options.onAnimationLoad) {
                    options.onAnimationLoad();
                }
            }
            options.loadingInProgress = false;

        } catch (e) {
            options.loadingInProgress = false;
            throw e;
        }
    }

}
